import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { FullCrudPage } from "@/components/admin/FullCrudPage";
import { NumberField, SelectField, TextField } from "@/components/admin/fields";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { listHauliers } from "@/services/api/hauliers";
import {
  createRateCard,
  deleteRateCard,
  getRateCard,
  getRateCardOverview,
  listRateCards,
  updateRateCard,
  type RateCardInput,
} from "@/services/api/rateCards";
import type { RateCard } from "@/services/api/types";

const RETURN_MODE_OPTIONS = [
  { value: "", label: "—" },
  { value: "percent", label: "% of standard" },
  { value: "flat", label: "Flat fee" },
];

interface RateCardForm {
  haulier_id: string;
  effective_from: string;
  effective_to: string;
  basis: string;
  distance_band: string;
  pallet_band_min: string;
  pallet_band_max: string;
  notice_hours_threshold: string;
  rate_standard: string;
  rate_emergency: string;
  rate_out_of_hours: string;
  rate_weekend: string;
  currency: string;
  return_mode: string;
  return_value: string;
}

const emptyForm: RateCardForm = {
  haulier_id: "",
  effective_from: "",
  effective_to: "",
  basis: "per_load",
  distance_band: "",
  pallet_band_min: "",
  pallet_band_max: "",
  notice_hours_threshold: "24",
  rate_standard: "",
  rate_emergency: "",
  rate_out_of_hours: "",
  rate_weekend: "",
  currency: "GBP",
  return_mode: "",
  return_value: "",
};

const BASIS_OPTIONS = [
  { value: "per_load", label: "Per load" },
  { value: "per_pallet", label: "Per pallet" },
];

function toPayload(form: RateCardForm): RateCardInput {
  const numberOrNull = (v: string) => (v.trim() === "" ? null : Number(v));
  return {
    haulier_id: Number(form.haulier_id),
    effective_from: form.effective_from,
    effective_to: form.effective_to || null,
    basis: form.basis,
    distance_band: form.distance_band || null,
    pallet_band_min: numberOrNull(form.pallet_band_min),
    pallet_band_max: numberOrNull(form.pallet_band_max),
    notice_hours_threshold: Number(form.notice_hours_threshold || 24),
    rate_standard: Number(form.rate_standard),
    rate_emergency: numberOrNull(form.rate_emergency),
    rate_out_of_hours: numberOrNull(form.rate_out_of_hours),
    rate_weekend: numberOrNull(form.rate_weekend),
    currency: form.currency || "GBP",
    return_mode: form.return_mode || null,
    return_value: numberOrNull(form.return_value),
  };
}

/** Haulier rate-card contracts: effective-dated pricing used to cost each load. */
export function RateCardsPage() {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const [selectedHaulier, setSelectedHaulier] = useState<number | null>(null);
  const [selectedCard, setSelectedCard] = useState<number | null>(null);
  const limit = 25;
  const haulierLimit = 500;

  const query = useQuery({
    queryKey: ["rate-cards", offset],
    queryFn: () => listRateCards({ limit, offset }),
  });
  const overview = useQuery({
    queryKey: ["rate-cards", "overview"],
    queryFn: getRateCardOverview,
  });
  const hauliers = useQuery({
    queryKey: ["hauliers", "all"],
    queryFn: () => listHauliers({ limit: haulierLimit }),
  });
  const haulierOptions = (hauliers.data?.items ?? []).map((h) => ({
    value: h.id.toString(),
    label: `${h.haulier_code} — ${h.name}`,
  }));
  const haulierName = (id: number | null) =>
    hauliers.data?.items.find((h) => h.id === id)?.name ?? "—";

  const columns: ColumnDef<RateCard>[] = [
    {
      accessorKey: "haulier_id",
      header: t("admin.haulier"),
      cell: ({ getValue }) => haulierName(getValue() as number | null),
    },
    { accessorKey: "effective_from", header: t("admin.effectiveFrom") },
    {
      accessorKey: "effective_to",
      header: t("admin.effectiveTo"),
      cell: ({ getValue }) => String(getValue() ?? "—"),
    },
    { accessorKey: "basis", header: t("admin.basis") },
    { accessorKey: "rate_standard", header: t("admin.rateStandard") },
  ];

  return (
    <div className="space-y-8">
    <FullCrudPage<RateCard, RateCardForm>
      title={t("nav.rateCards")}
      entityType="rate_card"
      getRecord={getRateCard}
      columns={columns}
      data={query.data?.items ?? []}
      total={query.data?.total ?? 0}
      isLoading={query.isLoading}
      limit={limit}
      offset={offset}
      onPageChange={setOffset}
      queryKey="rate-cards"
      emptyForm={emptyForm}
      toForm={(r) => ({
        haulier_id: String(r.haulier_id),
        effective_from: r.effective_from,
        effective_to: r.effective_to ?? "",
        basis: r.basis,
        distance_band: r.distance_band ?? "",
        pallet_band_min: r.pallet_band_min?.toString() ?? "",
        pallet_band_max: r.pallet_band_max?.toString() ?? "",
        notice_hours_threshold: r.notice_hours_threshold.toString(),
        rate_standard: r.rate_standard.toString(),
        rate_emergency: r.rate_emergency?.toString() ?? "",
        rate_out_of_hours: r.rate_out_of_hours?.toString() ?? "",
        rate_weekend: r.rate_weekend?.toString() ?? "",
        currency: r.currency,
        return_mode: r.return_mode ?? "",
        return_value: r.return_value?.toString() ?? "",
      })}
      canSubmit={(f) => Boolean(f.haulier_id && f.effective_from && f.rate_standard)}
      renderForm={(form, setForm) => (
        <div className="grid grid-cols-2 gap-4">
          <SelectField
            label={t("admin.haulier")}
            value={form.haulier_id}
            onChange={(v) => setForm({ ...form, haulier_id: v })}
            options={[{ value: "", label: "—" }, ...haulierOptions]}
          />
          <div className="space-y-2">
            <span className="text-sm font-medium">{t("admin.effectiveFrom")}</span>
            <input
              type="date"
              value={form.effective_from}
              onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
              className="h-9 w-full rounded-md border bg-background px-3 text-sm"
            />
          </div>
          <div className="space-y-2">
            <span className="text-sm font-medium">{t("admin.effectiveTo")}</span>
            <input
              type="date"
              value={form.effective_to}
              onChange={(e) => setForm({ ...form, effective_to: e.target.value })}
              className="h-9 w-full rounded-md border bg-background px-3 text-sm"
            />
          </div>
          <SelectField
            label={t("admin.basis")}
            value={form.basis}
            onChange={(v) => setForm({ ...form, basis: v })}
            options={BASIS_OPTIONS}
          />
          <TextField
            label={t("admin.distanceBand")}
            value={form.distance_band}
            onChange={(v) => setForm({ ...form, distance_band: v })}
          />
          <NumberField
            label={t("admin.palletBandMin")}
            value={form.pallet_band_min}
            onChange={(v) => setForm({ ...form, pallet_band_min: v })}
          />
          <NumberField
            label={t("admin.palletBandMax")}
            value={form.pallet_band_max}
            onChange={(v) => setForm({ ...form, pallet_band_max: v })}
          />
          <NumberField
            label={t("admin.noticeHoursThreshold")}
            value={form.notice_hours_threshold}
            onChange={(v) => setForm({ ...form, notice_hours_threshold: v })}
          />
          <NumberField
            label={t("admin.rateStandard")}
            value={form.rate_standard}
            onChange={(v) => setForm({ ...form, rate_standard: v })}
          />
          <NumberField
            label={t("admin.rateEmergency")}
            value={form.rate_emergency}
            onChange={(v) => setForm({ ...form, rate_emergency: v })}
          />
          <NumberField
            label={t("admin.rateOutOfHours")}
            value={form.rate_out_of_hours}
            onChange={(v) => setForm({ ...form, rate_out_of_hours: v })}
          />
          <NumberField
            label={t("admin.rateWeekend")}
            value={form.rate_weekend}
            onChange={(v) => setForm({ ...form, rate_weekend: v })}
          />
          <TextField
            label={t("admin.currency")}
            value={form.currency}
            onChange={(v) => setForm({ ...form, currency: v })}
          />
          <SelectField
            label={t("admin.returnRate")}
            value={form.return_mode}
            onChange={(v) => setForm({ ...form, return_mode: v })}
            options={RETURN_MODE_OPTIONS}
          />
          <NumberField
            label={
              form.return_mode === "percent"
                ? t("admin.returnPercent")
                : form.return_mode === "flat"
                  ? t("admin.returnFlat")
                  : t("admin.returnRate")
            }
            value={form.return_value}
            onChange={(v) => setForm({ ...form, return_value: v })}
          />
        </div>
      )}
      onSave={async (mode, form, record) =>
        mode === "edit" && record
          ? updateRateCard(record.id, toPayload(form))
          : createRateCard(toPayload(form))
      }
      onDelete={(record) => deleteRateCard(record.id)}
      recordTitle={(r) => `${haulierName(r.haulier_id)} · ${r.effective_from}`}
      detailFields={(r) => [
        { label: t("admin.haulier"), value: haulierName(r.haulier_id), field: "haulier_id" },
        { label: t("admin.effectiveFrom"), value: r.effective_from, field: "effective_from" },
        { label: t("admin.effectiveTo"), value: r.effective_to ?? "—", field: "effective_to" },
        { label: t("admin.basis"), value: r.basis, field: "basis" },
        { label: t("admin.distanceBand"), value: r.distance_band ?? "—", field: "distance_band" },
        { label: t("admin.palletBandMin"), value: r.pallet_band_min ?? "—", field: "pallet_band_min" },
        { label: t("admin.palletBandMax"), value: r.pallet_band_max ?? "—", field: "pallet_band_max" },
        {
          label: t("admin.noticeHoursThreshold"),
          value: r.notice_hours_threshold,
          field: "notice_hours_threshold",
        },
        { label: t("admin.rateStandard"), value: r.rate_standard, field: "rate_standard" },
        { label: t("admin.rateEmergency"), value: r.rate_emergency ?? "—", field: "rate_emergency" },
        {
          label: t("admin.rateOutOfHours"),
          value: r.rate_out_of_hours ?? "—",
          field: "rate_out_of_hours",
        },
        { label: t("admin.rateWeekend"), value: r.rate_weekend ?? "—", field: "rate_weekend" },
        { label: t("admin.currency"), value: r.currency, field: "currency" },
        {
          label: t("admin.returnRate"),
          value: r.return_mode
            ? `${r.return_value ?? "—"}${r.return_mode === "percent" ? "%" : ""}`
            : "—",
        },
      ]}
    />

    {/* Drill-down: select a haulier → its rate cards → a card's customers */}
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{t("admin.rateCardsByHaulier")}</h2>
        <span className="text-sm text-muted-foreground">{t("admin.selectHaulier")}</span>
      </div>
      {(overview.data ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("common.emptyState")}</p>
      ) : (
        <>
          {/* Haulier selector */}
          <div className="flex flex-wrap gap-2">
            {(overview.data ?? []).map((haulier) => (
              <Button
                key={haulier.id}
                variant={selectedHaulier === haulier.id ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setSelectedHaulier(haulier.id);
                  setSelectedCard(null);
                }}
              >
                {haulier.name}
                <Badge
                  variant={selectedHaulier === haulier.id ? "secondary" : "outline"}
                  className="ml-1.5"
                >
                  {haulier.rate_cards.length}
                </Badge>
              </Button>
            ))}
          </div>

          {/* Selected haulier's rate cards */}
          {selectedHaulier === null ? (
            <p className="text-sm text-muted-foreground">{t("admin.pickHaulierPrompt")}</p>
          ) : (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">
                  {overview.data?.find((h) => h.id === selectedHaulier)?.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const haulier = overview.data?.find((h) => h.id === selectedHaulier);
                  if (!haulier || haulier.rate_cards.length === 0) {
                    return (
                      <p className="text-sm text-muted-foreground">{t("admin.noRateCards")}</p>
                    );
                  }
                  return (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                            <th className="px-3 py-2">{t("admin.effectiveFrom")}</th>
                            <th className="px-3 py-2">{t("admin.effectiveTo")}</th>
                            <th className="px-3 py-2">{t("admin.basis")}</th>
                            <th className="px-3 py-2">{t("admin.rateStandard")}</th>
                            <th className="px-3 py-2">{t("admin.returnRate")}</th>
                            <th className="px-3 py-2">{t("admin.cseCustomers")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {haulier.rate_cards.map((card) => (
                            <tr
                              key={card.id}
                              className={`cursor-pointer border-b last:border-0 ${
                                selectedCard === card.id ? "bg-primary/10" : "hover:bg-muted/40"
                              }`}
                              onClick={() =>
                                setSelectedCard(selectedCard === card.id ? null : card.id)
                              }
                            >
                              <td className="px-3 py-2 font-medium">{card.effective_from}</td>
                              <td className="px-3 py-2">{card.effective_to ?? "—"}</td>
                              <td className="px-3 py-2">{card.basis}</td>
                              <td className="px-3 py-2">
                                {card.currency} {card.rate_standard}
                              </td>
                              <td className="px-3 py-2">
                                {card.return_mode
                                  ? `${card.return_value ?? "—"}${
                                      card.return_mode === "percent" ? "%" : ""
                                    }`
                                  : "—"}
                              </td>
                              <td className="px-3 py-2 text-muted-foreground">
                                {card.customers.length}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Selected rate card's customers */}
          {selectedCard === null ? (
            <p className="text-sm text-muted-foreground">{t("admin.pickRateCardPrompt")}</p>
          ) : (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{t("admin.customersOnCard")}</CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const card = overview.data
                    ?.find((h) => h.id === selectedHaulier)
                    ?.rate_cards.find((c) => c.id === selectedCard);
                  if (!card || card.customers.length === 0) {
                    return (
                      <p className="text-sm text-muted-foreground">{t("admin.noCustomersOnCard")}</p>
                    );
                  }
                  return (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                            <th className="px-3 py-2">{t("admin.customer")}</th>
                            <th className="px-3 py-2">{t("admin.record")}</th>
                            <th className="px-3 py-2">{t("admin.address")}</th>
                            <th className="px-3 py-2">{t("admin.postcode")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {card.customers.map((customer) => (
                            <tr key={customer.id} className="border-b last:border-0">
                              <td className="px-3 py-2 font-medium">{customer.name}</td>
                              <td className="px-3 py-2">{customer.customer_no}</td>
                              <td className="px-3 py-2 text-muted-foreground">
                                {customer.address ?? "—"}
                              </td>
                              <td className="px-3 py-2">{customer.postcode ?? "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  </div>
  );
}
